import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Snackbar from 'ui-component/extended/Snackbar';

import Breadcrumb from 'components/breadcrumb';
import Spinner from 'components/spinner';
import lang from 'constants/language';
import { Formik } from 'formik';
import useGQL from 'hooks/useGQL';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ERoutes from 'routes/ERoutes';
import { openSnackbar } from 'store/slices/snackbar';
import { BrandedAppPromoCodeForm } from './brandedAppPromoCodeForm';
import { brandedAppPromoCodeValidation } from './validationSchema';

const BrandedAppPromoCodeAddForm = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { open } = useSelector((state: RootState) => state.snackbar);
    const params = useParams();
    const { CREATE_BRANDED_APP_PROMO_CODE, UPDATE_BRANDED_APP_PROMO_CODE, GET_BRANDED_APP_PROMO_CODE_DETAIL } = useGQL();

    const [handleCreatePromoCode] = CREATE_BRANDED_APP_PROMO_CODE();
    const [handleUpdatePromoCode] = UPDATE_BRANDED_APP_PROMO_CODE();

    const [promoCode, setPromoCode] = useState({
        title: '',
        code: '',
        endType: '',
        endDate: null,
        endAfterOccurrence: null,
        setupDiscountMethod: '',
        setupDiscountAmount: '',
        subscriptionDiscountMethod: '',
        subscriptionDiscountAmount: '',
        subscriptionDiscountDuration: ''
    });

    const { loading } = GET_BRANDED_APP_PROMO_CODE_DETAIL(params.id as string, !params.id, (data) => {
        const discountCodeData = data?.getBrandedAppPromoCodeDetails;
        if (discountCodeData) {
            setPromoCode({
                title: discountCodeData.title || '',
                code: discountCodeData.code || '',
                endType: discountCodeData.endType || '',
                endDate: discountCodeData.endDate || null,
                endAfterOccurrence: discountCodeData.endAfterOccurrence || null,
                setupDiscountMethod: discountCodeData.setupDiscountMethod || '',
                setupDiscountAmount: discountCodeData.setupDiscountAmount ?? '',
                subscriptionDiscountMethod: discountCodeData.subscriptionDiscountMethod || '',
                subscriptionDiscountAmount: discountCodeData.subscriptionDiscountAmount ?? '',
                subscriptionDiscountDuration: discountCodeData.subscriptionDiscountDuration || ''
            });
        }
    });

    const handleSubmitForm = async (values) => {
        try {
            if (params.id) {
                await handleUpdatePromoCode({
                    variables: {
                        input: {
                            promoCodeId: params.id,
                            title: values.title
                        }
                    }
                });
                navigate(ERoutes.brandedAppPromoCodeList);
                dispatch(
                    openSnackbar({
                        open: true,
                        message: lang.MODAL_UPDATED_SUCCESS('Branded app promo code'),
                        anchorOrigin: { horizontal: 'center' },
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        }
                    })
                );
            } else {
                // Build create payload, cleaning empty/null values
                const createInput: any = {
                    title: values.title,
                    code: values.code,
                    endType: values.endType
                };

                if (values.endDate) {
                    createInput.endDate = values.endDate;
                }
                if (values.endAfterOccurrence) {
                    createInput.endAfterOccurrence = values.endAfterOccurrence;
                }
                if (values.setupDiscountMethod) {
                    createInput.setupDiscountMethod = values.setupDiscountMethod;
                    createInput.setupDiscountAmount = Number(values.setupDiscountAmount);
                }
                if (values.subscriptionDiscountMethod) {
                    createInput.subscriptionDiscountMethod = values.subscriptionDiscountMethod;
                    createInput.subscriptionDiscountAmount = Number(values.subscriptionDiscountAmount);
                    createInput.subscriptionDiscountDuration = values.subscriptionDiscountDuration;
                }

                await handleCreatePromoCode({
                    variables: {
                        input: createInput
                    }
                });
                navigate(ERoutes.brandedAppPromoCodeList);
                dispatch(
                    openSnackbar({
                        open: true,
                        message: lang.MODAL_CREATED_SUCCESS('Branded app promo code'),
                        anchorOrigin: { horizontal: 'center' },
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        }
                    })
                );
            }
        } catch (err: any) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.message,
                    anchorOrigin: { horizontal: 'center' },
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    }
                })
            );
        }
    };

    return (
        <>
            <Breadcrumb
                links={[{ name: 'Branded App Promo Codes', to: ERoutes.brandedAppPromoCodeList }]}
                display={params.id ? promoCode.title : 'Add branded app promo code'}
                isEdit={params.id ? true : false}
                loading={params.id ? (promoCode.title ? false : true) : false}
            />
            {open && <Snackbar />}
            {loading && params.id ? (
                <Spinner />
            ) : (
                <Formik
                    enableReinitialize
                    initialValues={promoCode}
                    validationSchema={brandedAppPromoCodeValidation}
                    onSubmit={handleSubmitForm}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, setValues }) => {
                        return (
                            <form onSubmit={handleSubmit}>
                                <BrandedAppPromoCodeForm
                                    values={values}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    touched={touched}
                                    setFieldValue={setFieldValue}
                                    errors={errors}
                                    setValues={setValues}
                                    isSubmitting={isSubmitting}
                                    paramsId={params.id ? true : false}
                                />
                            </form>
                        );
                    }}
                </Formik>
            )}
        </>
    );
};

export default BrandedAppPromoCodeAddForm;
